import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { AudioCaptureActions } from "@/components/note/floating-actions/audio-capture";
import { CleanUpActions } from "@/components/note/floating-actions/clean-up-actions";

interface VoiceActionsProps {
  editorStore: MemCommonEditorStore;
  onRequestClose?: () => void;
}

export const VoiceActions = observer<VoiceActionsProps>(function VoiceActions({
  editorStore,
  onRequestClose,
}) {
  const { store } = useAppStore();

  const noteId = editorStore.id;
  const noteRecording = store.audioRecordings.getNoteRecording({ noteId });
  const recording = noteRecording?.recording;
  const isActiveForNote = store.audio.activeRecordingNoteId === noteId;

  const isSuggestionAvailable = Boolean(editorStore.noteObservable?.isSuggestionActive);
  // Reducing layout shifts by waiting until everything is updated.
  const isSuggestionActive =
    isSuggestionAvailable && editorStore.isShowingDifferences && editorStore.editorContentRendered;
  const processingSuggestion = isSuggestionAvailable && !isSuggestionActive;
  const hasProcessed = Boolean(recording) && recording?.hasFinishedProcessing;
  const shouldShowAudioActions =
    isActiveForNote ||
    processingSuggestion ||
    // We show for recordings that are not fully settled yet.
    (!!recording && !recording.isTrashed && !hasProcessed);
  /**
   * If we have an active "suggestion" (e.g. a voice recording suggestion), we
   * prioritize showing that.
   */
  if (isSuggestionActive) {
    return <CleanUpActions editorStore={editorStore} />;
  }

  /**
   * Otherwise; If we have an active audio recording, show the audio actions.
   */
  if (shouldShowAudioActions) {
    return <AudioCaptureActions noteId={noteId} onRequestClose={onRequestClose} />;
  }

  /**
   * Otherwise, don't show any actions.
   */
  return null;
});
